import { api_host } from '../constants'
import { apiFetch } from './apiRequestHandler'

/**
 * Helper function to parse code for different types
 */
const parseCodeByType = (code, type, name) => {
  let parsedName = name;
  const isWatchType = type === 'watch' || type === 'watcher';
  // Convert escaped newlines to actual newlines
  let bodyOnly = code.replace(/\\n/g, '\n').trim();

  const extractWatchName = (text) => {
    const trimmed = text.trim();
    let match = trimmed.match(/^\s*['"]([^'"]+)['"]\s*:/);
    if (match) return match[1];
    match = trimmed.match(/^\s*\[\s*['"]?([^'"\]]+)['"]?\s*\]\s*:/);
    if (match) return match[1];
    match = trimmed.match(/^\s*([a-zA-Z_$][\w.$]*)\s*:/);
    if (match) return match[1];
    return null;
  };
  
  // For data properties, extract value if in "propertyName: value" format
  if (type === 'data') {
    // Check if code is in format "propertyName: value"
    const dataMatch = bodyOnly.match(/^([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(.+)$/s);
    if (dataMatch) {
      let valueOnly = dataMatch[2].trim();
      if (valueOnly.startsWith('[')) {
        // Normalize array strings (quote unquoted keys, single -> double quotes)
        valueOnly = valueOnly.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
        valueOnly = valueOnly.replace(/'/g, '"');
      }
      bodyOnly = valueOnly;
    } else if (bodyOnly.trim().startsWith('[')) {
      // Normalize raw array strings (quote unquoted keys, single -> double quotes)
      let rawValue = bodyOnly.trim();
      rawValue = rawValue.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
      rawValue = rawValue.replace(/'/g, '"');
      bodyOnly = rawValue;
    }
    return { parsedName, bodyOnly };
  }
  
  // For css/template, use code as-is
  if (type === 'css' || type === 'template') {
    return { parsedName, bodyOnly };
  }
  
  // For computed properties
  if (type === 'computed') {
    const computedWrapperMatch = bodyOnly.match(/^computed\s*:\s*\{([\s\S]*)\}\s*$/);
    if (computedWrapperMatch) {
      bodyOnly = computedWrapperMatch[1].trim();
    }
  }
  
  // Parse function bodies for methods/computed/watch
  let codeToParse = bodyOnly;
  let watchBodyOnly = null;
  const watchNameFromCode = isWatchType ? extractWatchName(bodyOnly) : null;
  const invalidWatchName = isWatchType && (!parsedName || /^\s*function\b/.test(parsedName) || /^\s*\(/.test(parsedName) || /=>/.test(parsedName));
  if (invalidWatchName && watchNameFromCode) {
    parsedName = watchNameFromCode;
  } else if (invalidWatchName) {
    parsedName = '';
  }

  const extractBlockBody = (text, startIndex) => {
    let braceCount = 1;
    let endIndex = startIndex;
    for (let i = startIndex + 1; i < text.length && braceCount > 0; i++) {
      if (text[i] === '{') braceCount++;
      if (text[i] === '}') braceCount--;
      if (braceCount === 0) {
        endIndex = i;
        break;
      }
    }
    if (endIndex > startIndex) {
      return text.substring(startIndex + 1, endIndex).trim();
    }
    return null;
  };

  if (isWatchType) {
    const genericKeyPrefixRegex = /^\s*(?:\[[^\]]+\]|['"][^'"]+['"]|[a-zA-Z_$][\w.$]*)\s*:\s*/;
    if (genericKeyPrefixRegex.test(codeToParse)) {
      codeToParse = codeToParse.replace(genericKeyPrefixRegex, '');
    }

    if (parsedName) {
      const nameKey = parsedName.replace(/\(\)$/, '').trim();
      if (nameKey) {
        const escapedName = nameKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const namePrefixRegex = new RegExp(`^\\s*(?:\\[\\s*)?['"]?${escapedName}['"]?(?:\\s*\\])?\\s*:\\s*`);
        if (namePrefixRegex.test(codeToParse)) {
          codeToParse = codeToParse.replace(namePrefixRegex, '');
        }
      }
    }

    const handlerIndex = codeToParse.search(/\bhandler\b/);
    if (handlerIndex !== -1) {
      const handlerSlice = codeToParse.slice(handlerIndex);
      const braceIndex = handlerSlice.indexOf('{');
      if (braceIndex !== -1) {
        const body = extractBlockBody(codeToParse, handlerIndex + braceIndex);
        if (body !== null) {
          watchBodyOnly = body;
        }
      } else {
        const arrowIndex = handlerSlice.indexOf('=>');
        if (arrowIndex !== -1) {
          let expr = handlerSlice.slice(arrowIndex + 2).trim();
          expr = expr.replace(/[,}]\s*$/, '').trim();
          if (expr) {
            watchBodyOnly = expr;
          }
        }
      }
    }
  }

  // Remove "function" keyword if present, but keep "async"
  codeToParse = codeToParse.replace(/^\s*function\b\s*/, '');
  bodyOnly = codeToParse;
  
  // Match function declaration: async? functionName(params) {
  const functionNameMatch = codeToParse.match(/^(async\s+)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(\([^)]*\))\s*\{/);
  
  if (functionNameMatch) {
    // Group 1 = async (optional), Group 2 = function name, Group 3 = params
    const asyncPrefix = functionNameMatch[1] || ''; // "async " or empty
    const functionName = functionNameMatch[2];
    const functionParams = functionNameMatch[3];
    const startIndex = functionNameMatch[0].length - 1;
    
    let braceCount = 1;
    let endIndex = startIndex;
    for (let i = startIndex + 1; i < codeToParse.length && braceCount > 0; i++) {
      if (codeToParse[i] === '{') braceCount++;
      if (codeToParse[i] === '}') braceCount--;
      if (braceCount === 0) {
        endIndex = i;
        break;
      }
    }
    
    if (endIndex > startIndex) {
      bodyOnly = codeToParse.substring(startIndex + 1, endIndex).trim();
      if (!isWatchType && !parsedName.includes('(')) {
        parsedName = `${asyncPrefix}${functionName}${functionParams}`;
      }
    }
  } else {
    const looksLikeFunction = /=>/.test(codeToParse)
      || /\bfunction\b/.test(codeToParse)
      || /^\s*[a-zA-Z_$][a-zA-Z0-9_$]*\s*\(/.test(codeToParse)
      || /^\s*\(/.test(codeToParse);

    if (looksLikeFunction) {
      const startIndex = codeToParse.indexOf('{');
      if (startIndex !== -1) {
        let braceCount = 1;
        let endIndex = startIndex;
        for (let i = startIndex + 1; i < codeToParse.length && braceCount > 0; i++) {
          if (codeToParse[i] === '{') braceCount++;
          if (codeToParse[i] === '}') braceCount--;
          if (braceCount === 0) {
            endIndex = i;
            break;
          }
        }
        if (endIndex > startIndex) {
          bodyOnly = codeToParse.substring(startIndex + 1, endIndex).trim();
        }
      }
    }

    if (parsedName && !parsedName.includes('(') && !isWatchType && type !== 'computed') {
      parsedName = `${parsedName}()`;
    }
  }
  
  // Remove parentheses from data/watch property names
  if ((type === 'data' || isWatchType) && parsedName) {
    parsedName = parsedName.replace(/\(\)$/, '');
  }
  
  // For computed: remove empty () but keep () with arguments
  if (type === 'computed' && parsedName) {
    // Remove empty parentheses: filteredUsers() -> filteredUsers
    // But keep parentheses with arguments: getUser(id) -> getUser(id)
    parsedName = parsedName.replace(/\(\)$/, '');
  }
  
  if (watchBodyOnly !== null) {
    bodyOnly = watchBodyOnly;
  }

  // Final cleanup - remove any remaining escaped newlines and trim
  bodyOnly = bodyOnly.replace(/\\n/g, '\n').trim();
  
  return { parsedName, bodyOnly };
};

/**
 * Handle new component creation responses
 */
const handleNewComponentResponse = (response, responseThreadId, responseTitle = null) => {
  const { message, component, components, isNewComponent } = response.result;
  
  // Build formatted response text
  let formattedResponse = message ? `${message}\n\n` : '';
  formattedResponse += `**New Component:** \`${component.name}\`\n\n`;
  
  if (component.summary) {
    formattedResponse += `**Description:** ${component.summary}\n\n`;
  }
  
  formattedResponse += `**Template:**\n\`\`\`html\n${component.template}\n\`\`\`\n\n`;
  
  if (components && components.length > 0) {
    formattedResponse += `**Components:**\n\n`;
    
    // Format each component
    const metadataComponents = [];
    
    components.forEach((comp, index) => {
      const { type, name: componentName, code, isNew, action, target } = comp;
      
      // Skip template (already shown above for new components)
      if (type === 'template') {
        return;
      }
      
      // Handle CSS with different actions
      if (type === 'css') {
        const actionLabel = action === 'replace_all'
          ? `**CSS Update** (Replace All)`
          : `**CSS Update** (Append)`;
        formattedResponse += `${actionLabel}\n\n`;
        if (code && code.trim()) {
          formattedResponse += `\`\`\`css\n${code}\n\`\`\`\n\n`;
        }
        
        // Add CSS to metadata for insert button
        metadataComponents.push({
          type: 'css',
          name: 'css',
          code: code || '',
          isNew: isNew !== undefined ? isNew : true,
          action: action || 'append',
          index
        });
        return;
      }
      
      // Parse the code
      const { parsedName, bodyOnly } = parseCodeByType(code || '', type, componentName || '');
      
      // Add to formatted response
      const actionLabel = action === 'replace' ? ' (Replace Existing)' : '';
      formattedResponse += `**${type.charAt(0).toUpperCase() + type.slice(1)}**: \`${parsedName}\`${actionLabel}\n\n`;
      if (code) {
        formattedResponse += `\`\`\`javascript\n${bodyOnly}\n\`\`\`\n\n`;
      }
      
      // Add to metadata array for insert buttons
      metadataComponents.push({
        type,
        name: parsedName,
        code: bodyOnly,
        isNew: isNew !== undefined ? isNew : false,
        action: action || 'add',
        target: target || null,
        index
      });
    });
    
    return {
      text: formattedResponse,
      metadata: {
        type: 'component',
        name: component.name,
        component: {
          name: component.name,
          template: component.template,
          is_global: component.is_global || false,
          summary: component.summary || '',
          props: component.props || [],
          deps: component.deps || []
        },
        components: metadataComponents,
        isNewComponent: isNewComponent !== undefined ? isNewComponent : true,
        isHistory: false
      },
      threadId: responseThreadId,
      title: responseTitle
    };
  }
  
  // If no components, just return the component info
  return {
    text: formattedResponse,
    metadata: {
      type: 'component',
      name: component.name,
      component: {
        name: component.name,
        template: component.template,
        is_global: component.is_global || false,
        summary: component.summary || '',
        props: component.props || [],
        deps: component.deps || []
      },
      components: [],
      isNewComponent: isNewComponent !== undefined ? isNewComponent : true,
      isHistory: false
    },
    threadId: responseThreadId,
    title: responseTitle
  };
};

/**
 * Handle multi-component feature responses
 */
const handleFeatureResponse = (response, responseThreadId, responseTitle = null) => {
  const { name, message, components, template } = response.result;
  
  // Build formatted response text
  let formattedResponse = message ? `${message}\n\n` : '';
  formattedResponse += `**Feature:** \`${name}\`\n\n`;
  
  // Handle complete template (always present for features)
  if (template) {
    formattedResponse += `**Template:**\n\`\`\`html\n${template}\n\`\`\`\n\n`;
  }
  
  if (components && components.length > 0) {
  formattedResponse += `**Components:**\n\n`;
  }
  
  // Format each component
  const metadataComponents = [];
  
  // Add template to metadata if present
  if (template) {
    metadataComponents.push({
      type: 'template',
      name: 'template',
      code: template,
      isNew: true,
      index: -1
    });
  }
  
  if (components && Array.isArray(components)) {
  components.forEach((component, index) => {
      const { type, name: componentName, code, isNew, action } = component;
    
      // Skip template type in components array (handled above)
    if (type === 'template') {
        return;
      }
      
      // Handle CSS with different actions
      if (type === 'css') {
        const actionLabel = action === 'replace_all'
          ? `**CSS Update** (Replace All)`
          : `**CSS Update** (Append)`;
        formattedResponse += `${actionLabel}\n\n`;
        if (code && code.trim()) {
          formattedResponse += `\`\`\`css\n${code}\n\`\`\`\n\n`;
        }
      
        // Add CSS to metadata for insert button
        metadataComponents.push({
          type: 'css',
          name: 'css',
          code: code || '',
          isNew: isNew !== undefined ? isNew : true,
          action: action || 'append',
          index
        });
        return;
      }
    
    // Parse the code
    const { parsedName, bodyOnly } = parseCodeByType(code || '', type, componentName || '');
    
    // Add to formatted response
    const actionLabel = action === 'replace' ? ' (Replace Existing)' : '';
    formattedResponse += `**${type.charAt(0).toUpperCase() + type.slice(1)}**: \`${parsedName}\`${actionLabel}\n\n`;
    if (code) {
      formattedResponse += `\`\`\`javascript\n${bodyOnly}\n\`\`\`\n\n`;
    }
    
    // Add to metadata array for insert buttons
    metadataComponents.push({
      type,
      name: parsedName,
      code: bodyOnly,
      isNew: isNew !== undefined ? isNew : false,
      action: action || 'add',
      index // Track index for UI
    });
  });
  }
  
  return {
    text: formattedResponse,
    metadata: {
      type: 'feature',
      name,
      template: template || null,
      components: metadataComponents
    },
    threadId: responseThreadId,
    title: responseTitle
  };
};

/**
 * Generates a conversational response from AI
 * @param {string} prompt - User's prompt/question
 * @param {Object} componentContext - Optional component context
 * @param {string} siteId - Site/Project ID
 * @param {string} uri - Site/Project URI
 * @param {string} threadId - Optional thread ID to continue conversation
 * @returns {Promise<Object>} AI response with text, metadata, and threadId
 */
export const generateConversationalResponse = async (prompt, componentContext = null, siteId = null, uri = null, threadId = null, forceNewThread = false) => {
  try {
    const requestBody = {
      prompt,
      componentContext,
      siteId,
      uri
    };
    
    // Include threadId if provided to continue the conversation
    if (threadId) {
      requestBody.threadId = threadId;
    }
    
    // Include forceNewThread if true to force creation of a new thread
    if (forceNewThread) {
      requestBody.forceNewThread = true;
    }
    
    const response = await apiFetch(`${api_host}/ai/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })

    // Extract threadId from response (could be at root level or in result)
    const responseThreadId = response.threadId || (response.result && response.result.threadId);
    
    // Extract title from response (could be at root level or in result)
    const responseTitle = response.title || (response.result && response.result.title);
    
    // Parse the response - backend returns { result: { message: "...", type: "...", code: "...", name: "..." }, threadId: "...", title: "...", ... }
    if (typeof response === 'string') {
      return { text: response, metadata: null, threadId: responseThreadId, title: responseTitle };
    } else if (response && response.result) {
      let { type, name, code, message, components } = response.result;
      
      // Handle unsupported library responses (e.g., Vuetify, Quasar, etc.)
      if (response.result.unsupportedLibrary) {
        const { unsupportedLibrary, referenceCode } = response.result;
        
        let formattedResponse = message ? `${message}\n\n` : '';
        
        if (referenceCode) {
          formattedResponse += `**Reference Code (${unsupportedLibrary}):**\n\`\`\`html\n${referenceCode}\n\`\`\`\n\n`;
          formattedResponse += `> **Tip:** Ask me to create an equivalent using vanilla Vue + HTML + CSS that this builder supports.`;
        }
        
        return {
          text: formattedResponse,
          metadata: {
            type: 'unsupported',
            unsupportedLibrary,
            referenceCode,
            isReferenceOnly: true
          },
          threadId: responseThreadId,
          title: responseTitle
        };
      }
      
      // Handle multi-component features
      if (type === 'feature' && components && Array.isArray(components)) {
        return handleFeatureResponse(response, responseThreadId, responseTitle);
      }
      
      // Handle new component creation
      if (type === 'component' && response.result.isNewComponent && response.result.component) {
        return handleNewComponentResponse(response, responseThreadId, responseTitle);
      }
      
      // Normalize type: if backend sends "question" but it's actually a data property,
      // detect it from message content or metadata
      if (type === 'question' && code && name) {
        // Check if message mentions "data property"
        if (message && message.toLowerCase().includes('data property')) {
          type = 'data';
        }
        // Check metadata for target type if available
        else if (response.meta && response.meta.plan && response.meta.plan.target) {
          const targetType = response.meta.plan.target.type;
          if (targetType && targetType !== 'unknown') {
            type = targetType;
          }
        }
      }
      
      // Handle case where type and name exist but code and message are null
      // This usually means the property/method already exists or needs clarification
      if (type && name && !code && !message) {
        const typeLabel = type === 'computed' ? 'computed property' : 
                          type === 'method' ? 'method' : 
                          type === 'watch' ? 'watcher' : type;
        const responseText = `The ${typeLabel} \`${name}\` already exists in this component. ` +
                           `If you'd like to modify it or create a different one, please provide more details.`;
        return { 
          text: responseText, 
          metadata: null, 
          threadId: responseThreadId 
        };
      }
      
      // If backend sends code (type: method/computed/watch/etc)
      if (code && type && type !== 'clarification') {
        // Parse the code to extract body only (remove function signature)
        let parsedName = name;
        let bodyOnly = code.trim();
        let codeToParse = bodyOnly;
        
        // For data properties, skip function parsing and extract value only
        if (type === 'data') {
          // Data properties are just values (arrays, objects, primitives)
          // Check if code is in format "propertyName: value"
          const dataMatch = code.trim().match(/^([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(.+)$/s);
          if (dataMatch) {
            // Extract just the value part
            let valueOnly = dataMatch[2].trim();
            // Normalize array strings (single -> double quotes, quote unquoted keys)
            if (valueOnly.startsWith('[')) {
              valueOnly = valueOnly.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
              valueOnly = valueOnly.replace(/'/g, '"');
            }
            bodyOnly = valueOnly;
          } else {
            let rawValue = code.trim();
            // Normalize raw array strings
            if (rawValue.startsWith('[')) {
              rawValue = rawValue.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');
              rawValue = rawValue.replace(/'/g, '"');
            }
            bodyOnly = rawValue;
          }
        }
        // For computed properties, handle cases where code might be wrapped in "computed: { ... }"
        else if (type === 'computed') {
          // Remove "computed:" prefix and outer braces if present
          const computedWrapperMatch = bodyOnly.match(/^computed\s*:\s*\{([\s\S]*)\}\s*$/);
          if (computedWrapperMatch) {
            codeToParse = computedWrapperMatch[1].trim();
            bodyOnly = codeToParse;
          }
        }
        
        // For methods/computed/watch, parse function bodies
        if (type !== 'data') {
          // Remove "function" keyword if present, but keep "async"
          codeToParse = codeToParse.replace(/^\s*function\s+/, '');
          bodyOnly = codeToParse; // Keep bodyOnly in sync
          
          // Extract function body if code includes full function declaration
          // Pattern: async? functionName() { body } or function functionName() { body }
          // Use a more robust approach to find the function body by locating the opening brace
          const functionNameMatch = codeToParse.match(/^(async\s+)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(\([^)]*\))\s*\{/);
          
          if (functionNameMatch) {
            // Group 1 = async (optional), Group 2 = function name, Group 3 = params
            const asyncPrefix = functionNameMatch[1] || ''; // "async " or empty
            const functionName = functionNameMatch[2];
            const functionParams = functionNameMatch[3];
            const startIndex = functionNameMatch[0].length - 1; // Position after opening brace
            
            // Find the matching closing brace
            let braceCount = 1;
            let endIndex = startIndex;
            for (let i = startIndex + 1; i < codeToParse.length && braceCount > 0; i++) {
              if (codeToParse[i] === '{') braceCount++;
              if (codeToParse[i] === '}') braceCount--;
              if (braceCount === 0) {
                endIndex = i;
                break;
              }
            }
            
            if (endIndex > startIndex) {
              // Extract the body (content between braces)
              bodyOnly = codeToParse.substring(startIndex + 1, endIndex).trim();
              
              // Update parsedName if it doesn't include parentheses (include async prefix)
              if (!parsedName.includes('(')) {
                parsedName = `${asyncPrefix}${functionName}${functionParams}`;
              }
            }
          } else {
            // If no function declaration found, assume it's already just the body
            // If name doesn't have parentheses, add them
            if (parsedName && !parsedName.includes('(')) {
              parsedName = `${parsedName}()`;
            }
          }
        }
        
        // Get isNew flag from response (defaults to false if not provided)
        // isNew: true = newly created/modified code, show "Insert Code" button
        // isNew: false = existing/retrieved/explanation code, no button needed
        const isNew = response.result.isNew !== undefined ? response.result.isNew : false;
        
        // Get action from response (defaults to 'add')
        // action: 'add' = insert new code
        // action: 'replace' = replace existing code
        const action = response.result.action || 'add';
        
        // For data properties, ensure name doesn't have parentheses
        if (type === 'data' && parsedName) {
          parsedName = parsedName.replace(/\(\)$/, '');
        }
        
        // Format the code response nicely
        let formattedResponse = message ? `${message}\n\n` : '';
        formattedResponse += `**Generated ${type}:**\n\n`;
        if (parsedName) {
          formattedResponse += `**Name:** \`${parsedName}\`\n\n`;
        }
        formattedResponse += `**Code:**\n\`\`\`javascript\n${bodyOnly}\n\`\`\``;
        
        // Return both formatted text and metadata with parsed values, including threadId, isNew, and action
        return {
          text: formattedResponse,
          metadata: { type, name: parsedName, code: bodyOnly, isNew, action },
          threadId: responseThreadId,
          title: responseTitle
        };
      }
      
      // If it's just a message (clarification/general)
      if (message) {
        return { text: message, metadata: null, threadId: responseThreadId, title: responseTitle };
      }
    } else if (response && response.text) {
      return { text: response.text, metadata: null, threadId: responseThreadId, title: responseTitle };
    } else if (response && response.response) {
      return { text: response.response, metadata: null, threadId: responseThreadId, title: responseTitle };
    }

    throw new Error('Invalid response format from backend');
  } catch (error) {
    console.error("Error generating conversational response:", error);
    throw new Error(`Failed to generate response: ${error.message}`);
  }
};

/**
 * Fetches chat history (list of threads) for a site/component with pagination
 * @param {string} siteId - Site/Project ID
 * @param {string} uri - Site/Project URI
 * @param {number} limit - Number of chats per page (default 20)
 * @param {string} before - Cursor: fetch chats older than this timestamp
 * @returns {Promise<Object>} Object with { chats, hasMore, nextCursor }
 */
export const fetchChatHistory = async (siteId = null, uri = null, limit = 20, before = null) => {
  try {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    if (uri) params.append('uri', uri);
    if (limit) params.append('limit', limit.toString());
    if (before) params.append('before', before);
    
    const queryString = params.toString();
    const url = `${api_host}/ai/history${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Handle new paginated response format
    if (response && response.chats) {
      return {
        chats: response.chats,
        hasMore: response.hasMore || false,
        nextCursor: response.nextCursor || null
      };
    }
    
    // Handle legacy response formats (backwards compatibility)
    let chats = [];
    if (Array.isArray(response)) {
      chats = response;
    } else if (response && Array.isArray(response.threads)) {
      chats = response.threads;
    } else if (response && Array.isArray(response.data)) {
      chats = response.data;
    } else if (response && response.history) {
      chats = response.history;
    }

    return {
      chats,
      hasMore: false,
      nextCursor: null
    };
  } catch (error) {
    console.error("Error fetching chat history:", error);
    throw new Error(`Failed to fetch chat history: ${error.message}`);
  }
};

/**
 * Fetches messages for a specific thread
 * @param {string} threadId - Thread ID
 * @param {string} siteId - Site/Project ID
 * @param {string} uri - Site/Project URI
 * @returns {Promise<Array>} Array of message objects
 */
export const fetchThreadMessages = async (threadId, siteId = null, uri = null) => {
  try {
    const params = new URLSearchParams();
    params.append('threadId', threadId);
    if (siteId) params.append('siteId', siteId);
    if (uri) params.append('uri', uri);
    
    const url = `${api_host}/ai/thread/${threadId}/messages?${params.toString()}`;
    
    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    } else if (response && Array.isArray(response.messages)) {
      return response.messages;
    } else if (response && Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  } catch (error) {
    console.error("Error fetching thread messages:", error);
    throw new Error(`Failed to fetch thread messages: ${error.message}`);
  }
};
